import * as React from "react";
import { LoginHeroSlide } from "./LoginHeroCarousel";

export interface LoginTestimonial {
  quote: string;
  author: string;
  handle: string;
  role: string;
  followers: string;
  avatar: string;
}

export interface LoginTestimonialSlideProps {
  testimonial: LoginTestimonial;
  className?: string;
}

export function LoginTestimonialSlide({
  testimonial,
  className,
}: LoginTestimonialSlideProps) {
  return (
    <LoginHeroSlide
      className={["px-5 pt-3 pb-10 sm:pt-4 sm:pb-12 flex items-center justify-center min-h-full h-auto bg-transparent", className]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="flex items-center justify-center min-h-full h-auto w-full bg-[#FFFAEF]">
        <div className="relative z-10 flex min-h-[232px] sm:min-h-[260px] h-auto w-full flex-col justify-between overflow-hidden rounded-lg border border-black bg-white px-5 py-4 sm:px-6 sm:py-5 shadow-[4px_4px_0px_0px_#000000] transition-all duration-200 group">
          <div className="z-10 flex items-center justify-between">
            <div className="flex gap-0.5">
              {[...Array(5)].map((_, index) => (
                <svg
                  key={index}
                  className="h-4 w-4 fill-current text-[#F59E0B] transition-transform duration-200 group-hover:scale-110"
                  viewBox="0 0 20 20"
                >
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              ))}
            </div>
            <div className="z-10 flex items-center gap-1 rounded-full bg-[#ECFDF3] px-2 py-0.5 text-[8px] sm:text-[9px] font-extrabold uppercase tracking-wider text-[#027A48]">
              <svg
                className="h-2.5 w-2.5 text-[#12B76A]"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4.5 12.75l6 6 9-13.5"
                />
              </svg>
              <span>Verified Creator</span>
            </div>
          </div>

          <div className="relative z-10 mt-2.5 flex flex-1 flex-col justify-center">
            <div className="absolute bottom-0.5 left-0 top-0.5 w-[2px] rounded-full bg-[#ED5AA9]" />
            <div className="relative pl-4 pr-1 sm:pl-5 sm:pr-2">
              <p className="relative z-10 text-[14px] sm:text-[15px] font-medium italic leading-[1.45] text-[#344054]">
                "
                {testimonial.quote.split("artha.link").map((part, index, parts) => (
                  <span key={index}>
                    {part}
                    {index < parts.length - 1 ? (
                      <span className="font-bold text-[#ED5AA9]">artha.link</span>
                    ) : null}
                  </span>
                ))}
                "
              </p>
              <div className="pointer-events-none absolute bottom-[-4px] right-0 z-0 select-none text-[#ED5AA9] opacity-[0.06] transition-transform duration-300 group-hover:scale-110">
                <svg width="36" height="36" viewBox="0 0 32 32" fill="currentColor">
                  <path d="M10 8c-3.3 0-6 2.7-6 6v10h10V14H8c0-2.2 1.8-4 4-4V8zm14 0c-3.3 0-6 2.7-6 6v10h10V14h-6c0-2.2 1.8-4 4-4V8z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="z-10 mt-2.5 flex items-center gap-2.5 border-t border-[#EAECF0] pt-2.5 sm:mt-3 sm:gap-3 sm:pt-3">
            <div className="relative shrink-0">
              <img
                src={testimonial.avatar}
                alt={testimonial.author}
                className="h-9 w-9 sm:h-10 sm:w-10 rounded-full border border-black/5 object-cover"
              />
            </div>
            <div className="flex min-w-0 flex-1 flex-col justify-center">
              <div className="flex items-center gap-1.5">
                <h4 className="truncate text-[13px] sm:text-sm font-bold text-[#101828]">
                  {testimonial.author}
                </h4>
                <svg
                  className="h-[14px] w-[14px] shrink-0 text-[#1D9BF0]"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M22.5 12.5c0-1.58-.875-2.95-2.148-3.6.154-.435.238-.905.238-1.4 0-2.21-1.71-3.99-3.818-3.99-.48 0-.94.1-1.348.27C14.825 2.515 13.512 1.5 12 1.5s-2.825 1.015-3.422 2.28c-.407-.17-.867-.27-1.348-.27-2.108 0-3.818 1.78-3.818 3.99 0 .495.084.965.238 1.4-1.273.65-2.148 2.02-2.148 3.6 0 1.58.875 2.95 2.148 3.6-.154.435-.238.905-.238 1.4 0 2.21 1.71 3.99 3.818 3.99.48 0 .94-.1 1.348-.27.597 1.265 1.91 2.28 3.422 2.28s2.825-1.015 3.422-2.28c.407.17.867.27 1.348.27 2.108 0 3.818-1.78 3.818-3.99 0-.495-.084-.965-.238-1.4 1.273-.65 2.148-2.02 2.148-3.6zm-12.5 4L6 12.5l1.5-1.5 2.5 2.5 6.5-6.5 1.5 1.5-8 8z" />
                </svg>
              </div>
              <div className="flex flex-col">
                <span className="truncate text-[10px] sm:text-[11px] font-semibold text-[#F79009]">
                  {testimonial.handle}
                </span>
                <p className="truncate text-[10px] sm:text-[11px] font-medium text-[#475467]">
                  {testimonial.role}
                </p>
              </div>
            </div>
            <div className="z-10 shrink-0 rounded-full bg-[#FEF3C7] px-2 py-0.5 text-[9px] sm:text-[10px] font-extrabold text-[#B45309]">
              {testimonial.followers}
            </div>
          </div>
        </div>
      </div>
    </LoginHeroSlide>
  );
}
